import { IconCheck, IconHistory, IconPencil, IconPlus } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { type FormEvent, type RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/common';

import type { AuditEvent, Entry, EntryKind, Visit } from '@/types/api';
import { describeEntry } from '@/utils/dental';
import type { PendingRecord, RecordPatch } from '../hooks/useVisitRecord';
import { EntryItem } from './EntryItem';
import { formatDateTime, formatRsd } from './format';
import { VisitRecord } from './VisitRecord';

type Props = {
  /** The visit on the server; null while its first entries are still unsent. */
  visit: Visit | null;
  entries: Entry[];
  /** Who recorded the entries when there is no visit (and doctor) yet. */
  fallbackAuthor: string | null;
  busyId: string | null;
  onEdit: (e: Entry) => void;
  onDelete: (e: Entry) => void;
  /** Confirms the visit (first time) or changes its price; resolves true when saved. */
  onPrice: (visit: Visit, priceRsd: number) => Promise<boolean>;
  loadLog: (visit: Visit) => Promise<AuditEvent[]>;
  /** Add a forgotten procedure to this visit after it was confirmed. */
  onAdd: (visit: Visit) => void;
  /** "Fiskal" checked or unchecked. */
  onFiscal: (visit: Visit, fiscal: boolean) => Promise<boolean>;
  /** Focused when dictation ends, so the price can be entered at once. */
  priceRef?: RefObject<HTMLInputElement | null>;
  /** A price said while dictating: fills the open visit's price field, the doctor confirms it. */
  spokenPrice?: SpokenPrice | null;
  /** Anamnesis and diagnoses (#23); resolves true when saved. */
  onRecord: (visit: Visit, patch: RecordPatch) => Promise<boolean>;
  /** Dictated anamnesis or diagnoses not saved yet (only on the visit of now). */
  pendingRecord?: PendingRecord | null;
  onRetryRecord?: () => void;
};

/** A new object each time it is said, so the same amount said again fills the field again. */
export type SpokenPrice = { price: number };

/** Digits only; '' while the field is empty. */
const parsePrice = (text: string): number | null => (/^\d{1,9}$/.test(text) ? Number(text) : null);

function PriceForm({
  initial,
  spoken,
  submitLabel,
  onSubmit,
  onCancel,
  inputRef,
}: {
  initial: number | null;
  spoken?: SpokenPrice | null;
  submitLabel: string;
  onSubmit: (price: number) => Promise<boolean>;
  onCancel?: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => {
    const start = spoken?.price ?? initial;
    return start === null ? '' : String(start);
  });
  const [busy, setBusy] = useState(false);
  const price = parsePrice(text.trim());

  useEffect(() => {
    if (spoken) setText(String(spoken.price));
  }, [spoken]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (price === null) return;
    setBusy(true);
    const ok = await onSubmit(price);
    setBusy(false);
    if (ok) onCancel?.();
  };

  return (
    <form className="price-form" onSubmit={submit}>
      <label htmlFor="visit-price" className="field__label">
        {t('chart.visit.price')}
      </label>
      <div className="price-form__row">
        <input
          id="visit-price"
          ref={inputRef}
          className="input"
          inputMode="numeric"
          autoComplete="off"
          placeholder="0"
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))}
        />
        <span className="price-form__unit">RSD</span>
        <button type="submit" className="btn btn--success" disabled={busy || price === null}>
          <IconCheck size={18} /> {submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}

type LoggedEntry = Parameters<typeof describeEntry>[0] & { kind?: EntryKind };

const kindLabel = (t: TFunction, kind: EntryKind) =>
  t(kind === 'EXISTING' ? 'chart.kind.existing' : 'chart.kind.performed');

/** "16 · Plomba O → 16 · Plomba MO · Zatečeno → Urađeno" */
function correctionDetail(t: TFunction, before?: LoggedEntry, after?: LoggedEntry): string {
  if (!(before && after)) return '';
  const was = describeEntry(before);
  const now = describeEntry(after);
  const what = was === now ? now : `${was} → ${now}`;
  return before.kind && after.kind && before.kind !== after.kind
    ? `${what} · ${kindLabel(t, before.kind)} → ${kindLabel(t, after.kind)}`
    : what;
}

/** A price change ("0 → 4 500 RSD"), or the "Fiskal" flag set or removed. */
function visitUpdateDetail(t: TFunction, d: Record<string, unknown>): string {
  if (Array.isArray(d.fields)) {
    return d.fields
      .map((f) => t(`chart.visitRecord.field.${String(f)}`, { defaultValue: String(f) }))
      .join(', ');
  }
  const before = (d.before ?? {}) as Record<string, unknown>;
  const after = (d.after ?? {}) as Record<string, unknown>;
  if (typeof after.fiscal === 'boolean') {
    return after.fiscal ? t('chart.visit.log.fiscalOn') : t('chart.visit.log.fiscalOff');
  }
  const price = (v: unknown) => (typeof v === 'number' ? formatRsd(v) : '—');
  return `${price(before.priceRsd)} → ${price(after.priceRsd)}`;
}

/** What an event changed, in a few words ("16: karijes → plomba", "0 → 4 500 RSD"). */
function logDetail(t: TFunction, e: AuditEvent, entries: Map<string, Entry>): string {
  const d = (e.data ?? {}) as Record<string, unknown>;
  const entry = e.entityId ? entries.get(e.entityId) : undefined;
  const price = (v: unknown) => (typeof v === 'number' ? formatRsd(v) : '—');
  switch (e.action) {
    case 'VISIT_CONFIRM':
      return price(d.priceRsd);
    case 'VISIT_UPDATE':
      return visitUpdateDetail(t, d);
    case 'ENTRY_CORRECT':
      return correctionDetail(
        t,
        d.before as LoggedEntry | undefined,
        d.after as LoggedEntry | undefined
      );
    case 'ENTRY_REJECT':
      return [entry ? describeEntry(entry) : '', d.reason ? String(d.reason) : '']
        .filter(Boolean)
        .join(' · ');
    case 'TRANSCRIPT_EDIT':
      return t('chart.visit.log.transcript');
    default:
      return entry ? describeEntry(entry) : '';
  }
}

function VisitLog({
  visit,
  entries,
  load,
}: {
  visit: Visit;
  entries: Entry[];
  load: Props['loadLog'];
}) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Reload when the visit changes (price, confirmation) or its entries do.
  const stamp = `${visit.status}:${visit.priceRsd}:${visit.fiscal}:${visit.anamnesis}:${visit.diagnoses.length}:${entries.map((e) => `${e.id}${e.status}${e.corrected}`).join()}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the visit or its entries change
  useEffect(() => {
    let live = true;
    load(visit)
      .then((list) => live && setEvents(list))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [visit, load, stamp]);

  if (failed) return <p className="small muted">{t('chart.visit.log.unavailable')}</p>;
  if (!events) return <Spinner />;
  const byId = new Map(entries.map((e) => [e.id, e]));
  return (
    <ol className="visit-log">
      {events.map((e) => (
        <li key={e.id} className="visit-log__item">
          <span className="visit-log__when">{formatDateTime(e.createdAt)}</span>
          <span>
            <strong>{e.user?.name ?? '—'}</strong> ·{' '}
            {t(`audit.action.${e.action}`, { defaultValue: e.action })}
            {(() => {
              const detail = logDetail(t, e, byId);
              return detail ? <span className="muted"> · {detail}</span> : null;
            })()}
          </span>
        </li>
      ))}
    </ol>
  );
}

function SignOff({
  visit,
  onPrice,
  priceRef,
  spokenPrice,
}: {
  visit: Visit;
  onPrice: Props['onPrice'];
  priceRef?: Props['priceRef'];
  spokenPrice?: Props['spokenPrice'];
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  if (visit.status === 'OPEN') {
    return (
      <PriceForm
        initial={visit.priceRsd}
        spoken={spokenPrice}
        submitLabel={t('chart.visit.confirm')}
        onSubmit={(price) => onPrice(visit, price)}
        inputRef={priceRef}
      />
    );
  }
  if (editing) {
    return (
      <PriceForm
        initial={visit.priceRsd}
        submitLabel={t('common.save')}
        onSubmit={(price) => onPrice(visit, price)}
        onCancel={() => setEditing(false)}
      />
    );
  }
  return (
    <div className="visit-confirmed">
      <IconCheck size={20} aria-hidden="true" />
      <div className="grow">
        <strong>{formatRsd(visit.priceRsd ?? 0)}</strong>
        <div className="small muted">
          {t('chart.visit.confirmedBy', {
            name: visit.confirmedBy?.name ?? '—',
            at: formatDateTime(visit.confirmedAt),
          })}
        </div>
      </div>
      <button
        type="button"
        className="icon-btn icon-btn--sm"
        onClick={() => setEditing(true)}
        aria-label={t('chart.visit.editPrice')}
        title={t('chart.visit.editPrice')}
      >
        <IconPencil size={18} />
      </button>
    </div>
  );
}

/** "Fiskal": the visit goes into the future e-karton export. Visible to everyone, logged. */
function FiscalToggle({ visit, onChange }: { visit: Visit; onChange: Props['onFiscal'] }) {
  const { t } = useTranslation();
  // Shown at once; put back when the server refuses.
  const [checked, setChecked] = useState(visit.fiscal);
  // One change at a time, so two quick clicks cannot reach the server out of order.
  const [pending, setPending] = useState(false);
  useEffect(() => setChecked(visit.fiscal), [visit.fiscal]);
  return (
    <label className="check visit-card__fiscal">
      <input
        type="checkbox"
        checked={checked}
        disabled={pending}
        onChange={async (e) => {
          const next = e.target.checked;
          setChecked(next);
          setPending(true);
          try {
            if (!(await onChange(visit, next))) setChecked(!next);
          } finally {
            setPending(false);
          }
        }}
      />
      <span>{t('chart.visit.fiscal')}</span>
    </label>
  );
}

/** Dictated anamnesis or diagnoses on their way to the visit (or not saved: retry). */
function PendingRecordNote({ record, onRetry }: { record: PendingRecord; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="visit-record__pending" role="status">
      <span className="small">
        {t(record.failed ? 'chart.visitRecord.notSaved' : 'chart.visitRecord.pending')}
      </span>
      {record.anamnesis.length > 0 && <p>{record.anamnesis.join(' ')}</p>}
      {record.diagnoses.length > 0 && <p className="icd-code">{record.diagnoses.join(', ')}</p>}
      {record.failed && onRetry && (
        <button type="button" className="btn btn--sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

/**
 * One visit: who and when (once), its entries, the total price that confirms
 * it, and its change log. Entries and price stay editable after confirmation.
 */
export function VisitCard(p: Props) {
  const { t } = useTranslation();
  const [logOpen, setLogOpen] = useState(false);
  const who = p.visit?.dentist?.name ?? p.fallbackAuthor;
  const when = p.visit?.startedAt ?? p.entries[0]?.createdAt ?? null;
  const shown = p.entries.filter((e) => e.status !== 'REJECTED');

  return (
    <section className="card visit-card">
      <div className="card__head">
        <h2>{t('chart.visit.title')}</h2>
        {p.visit?.fiscal && <span className="badge badge--info">{t('chart.visit.fiscal')}</span>}
        {p.visit?.status === 'CONFIRMED' && (
          <span className="badge badge--approved">{t('chart.visit.confirmed')}</span>
        )}
      </div>
      {(who || when) && (
        <p className="visit-card__who small muted">
          {[who, when ? formatDateTime(when) : null].filter(Boolean).join(' · ')}
        </p>
      )}

      {shown.length ? (
        <ul className="list">
          {shown.map((e) => (
            <li key={e.clientId}>
              <EntryItem
                entry={e}
                actions={{
                  onEdit: () => p.onEdit(e),
                  onDelete: () => p.onDelete(e),
                  busy: p.busyId === e.id,
                }}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">{t('chart.visit.empty')}</p>
      )}

      {p.pendingRecord && (
        <PendingRecordNote
          record={p.pendingRecord}
          onRetry={p.visit ? p.onRetryRecord : undefined}
        />
      )}

      {p.visit ? (
        <>
          {/* Under the visit's work it adds to, apart from the diagnoses. */}
          {p.visit.status === 'CONFIRMED' && (
            <button
              type="button"
              className="btn btn--sm visit-card__add"
              onClick={() => p.visit && p.onAdd(p.visit)}
            >
              <IconPlus size={16} /> {t('chart.visit.add')}
            </button>
          )}
          <hr className="divider" />
          <VisitRecord visit={p.visit} entries={p.entries} onSave={p.onRecord} />
          <hr className="divider" />
          <SignOff
            visit={p.visit}
            onPrice={p.onPrice}
            priceRef={p.priceRef}
            spokenPrice={p.spokenPrice}
          />
          <FiscalToggle visit={p.visit} onChange={p.onFiscal} />
          <button
            type="button"
            className="btn btn--sm visit-card__log-toggle"
            aria-expanded={logOpen}
            onClick={() => setLogOpen(!logOpen)}
          >
            <IconHistory size={16} /> {t('chart.visit.log.title')}
          </button>
          {logOpen && <VisitLog visit={p.visit} entries={p.entries} load={p.loadLog} />}
        </>
      ) : (
        shown.length > 0 && <p className="small muted">{t('chart.visit.notSynced')}</p>
      )}
    </section>
  );
}
